import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';

interface BaseProps {
  label: ReactNode;
  /** Texto de ayuda bajo el campo. Se enlaza con aria-describedby. */
  hint?: ReactNode;
  error?: string;
  required?: boolean;
}

interface TextFieldProps extends BaseProps {
  /** Teclado numerico en movil sin las restricciones de type=number. */
  inputMode?: 'decimal' | 'numeric';
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'number' | 'date' | 'email' | 'tel' | 'password';
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  /** Unidad o simbolo que se pinta a la derecha del campo. */
  suffix?: string;
  disabled?: boolean;
}

export function TextField({
  inputMode,
  label,
  hint,
  error,
  required,
  value,
  onChange,
  type = 'text',
  placeholder,
  min,
  max,
  step,
  suffix,
  disabled,
}: TextFieldProps): JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;

  return (
    <div className={`field ${error ? 'field--error' : ''}`.trim()}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required && <span className="field__required" aria-hidden="true"> *</span>}
      </label>
      <div className="field__control">
        <input
          id={id}
          className="input"
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          min={min}
          max={max}
          step={step}
          required={required}
          disabled={disabled}
          aria-describedby={hint || error ? hintId : undefined}
          aria-invalid={error ? true : undefined}
          inputMode={inputMode ?? (type === 'number' ? 'decimal' : undefined)}
        />
        {suffix && <span className="field__suffix">{suffix}</span>}
      </div>
      {(error || hint) && (
        <p className="field__hint" id={hintId}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

export interface SelectOption {
  value: string;
  label: string;
  /** Accion («+ Añadir proveedor») que se queda a la vista aunque se este buscando otra cosa. */
  fija?: boolean;
  contexto?: string;
}

interface SelectFieldProps extends BaseProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  disabled?: boolean;  /** Opciones agrupadas (optgroup). Si se pasa, tiene prioridad sobre options. */
  groups?: Array<{ label: string; options: Array<{ value: string; label: string; contexto?: string }> }>;
}

export function SelectField({
  label,
  hint,
  error,
  required,
  value,
  onChange,
  options,
  groups,
  disabled,
}: SelectFieldProps): JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;

  return (
    <div className={`field ${error ? 'field--error' : ''}`.trim()}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required && <span className="field__required" aria-hidden="true"> *</span>}
      </label>
      <SmartSelect
        id={id}
        value={value}
        onChange={onChange}
        options={options}
        groups={groups}
        disabled={disabled}
        describedBy={hint || error ? hintId : undefined}
      />
      {(error || hint) && (
        <p className="field__hint" id={hintId}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

interface CheckboxFieldProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
}

export function CheckboxField({
  label,
  checked,
  onChange,
  hint,
}: CheckboxFieldProps): JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className="field field--check">
      <input
        id={id}
        type="checkbox"
        className="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        aria-describedby={hint ? hintId : undefined}
      />
      <div className="field--check__content">
        <label className="field__label field__label--inline" htmlFor={id}>
          {label}
        </label>
        {hint && <p className="field__hint" id={hintId}>{hint}</p>}
      </div>
    </div>
  );
}

/** Agrupa campos en filas de dos columnas dentro de un formulario. */
export function FieldRow({ children }: { children: ReactNode }): JSX.Element {
  return <div className="field-row">{children}</div>;
}

/* ------------------------------------------------------------------ */
/* Selector con busqueda                                               */
/* ------------------------------------------------------------------ */

interface SearchSelectProps {
  label: ReactNode;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Cuantas opciones se pintan como maximo. Es un tope de RENDER: con mas
 * coincidencias se le pide al usuario que afine, en vez de escupir una lista
 * de doscientas que tampoco sirve de nada.
 */
const MAX_VISIBLES = 50;

/**
 * A partir de cuantas opciones un desplegable pasa a ser buscable. Por debajo
 * se queda el `<select>` del sistema: buscar entre «Fijo» y «Variable» es peor
 * que elegir, y en el movil el selector nativo es mejor que cualquier lista.
 */
export const BUSCABLE_DESDE = 8;

/** Sin acentos ni mayusculas: «limon» encuentra «Limón». */
const plano = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

interface OpcionPlana {
  value: string;
  label: string;
  group?: string;
  /** Acciones («➕ Crear…») que se quedan SIEMPRE a la vista, se busque lo que se busque. */
  fija?: boolean;
  /**
   * De quien cuelga la opcion («↳ Bodega» → «Compras materia prima»). En la
   * lista entera lo dice la posicion; al filtrar la posicion se pierde, asi
   * que entonces se escribe al lado, y tambien se puede buscar por el.
   */
  contexto?: string;
}

interface SmartSelectProps {
  value: string;
  onChange: (value: string) => void;
  options?: Array<{ value: string; label: string; fija?: boolean; contexto?: string }>;
  groups?: Array<{ label: string; options: Array<{ value: string; label: string; contexto?: string }> }>;
  disabled?: boolean;
  id?: string;
  className?: string;
  placeholder?: string;
  ariaLabel?: string;
  describedBy?: string;
  /** Buscable aunque la lista sea corta (o nunca, con `false`). */
  buscable?: boolean;
}

/**
 * EL desplegable de la aplicacion (21 sep 2026, Pol: «todos deberian ser
 * buscables; encontrar aqui un proveedor o un ingrediente es un rollo»).
 *
 * Con pocas opciones es un `<select>` nativo; desde BUSCABLE_DESDE, un campo
 * en el que se escribe y la lista se filtra. Mismo contrato en los dos casos
 * —value, onChange(value), options o groups—, asi que quien lo usa no decide
 * nada: si el catalogo crece, el desplegable se vuelve buscable solo.
 */
export function SmartSelect({
  value,
  onChange,
  options = [],
  groups,
  disabled,
  id,
  className,
  placeholder,
  ariaLabel,
  describedBy,
  buscable,
}: SmartSelectProps): JSX.Element {
  const planas: OpcionPlana[] = useMemo(
    () => (groups ? groups.flatMap((g) => g.options.map((o) => ({ ...o, group: g.label }))) : options),
    [groups, options],
  );
  const conBusqueda = buscable ?? planas.length >= BUSCABLE_DESDE;

  if (!conBusqueda) {
    return (
      <select
        id={id}
        className={className ?? 'input'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
      >
        {groups
          ? groups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))
          : options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
      </select>
    );
  }
  return (
    <Buscador
      id={id}
      className={className}
      value={value}
      onChange={onChange}
      options={planas}
      disabled={disabled}
      placeholder={placeholder}
      ariaLabel={ariaLabel}
      describedBy={describedBy}
    />
  );
}

function Buscador({
  id: idProp,
  className,
  value,
  onChange,
  options,
  disabled,
  placeholder,
  ariaLabel,
  describedBy,
}: {
  id?: string;
  className?: string;
  value: string;
  onChange: (value: string) => void;
  options: OpcionPlana[];
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  describedBy?: string;
}): JSX.Element {
  const propio = useId();
  const id = idProp ?? propio;
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(0);
  const caja = useRef<HTMLDivElement>(null);
  const lista = useRef<HTMLUListElement>(null);

  const elegido = options.find((o) => o.value === value);
  const filtradas = useMemo(() => {
    const aguja = plano(texto.trim());
    if (aguja === '') return options;
    // Todas las palabras, en cualquier orden: «tomate huerto» encuentra
    // «Tomate bola · Caja de 10 kg · El Huerto».
    const palabras = aguja.split(/\s+/);
    return options.filter((o) => {
      if (o.fija) return true;
      const pajar = plano(`${o.label} ${o.contexto ?? ''} ${o.group ?? ''}`);
      return palabras.every((p) => pajar.includes(p));
    });
  }, [options, texto]);
  /*
   * Las que se pintan de verdad. Todo lo que navega el teclado y lo que elige
   * Enter sale de aqui: si el recorrido fuera sobre `filtradas`, con muchas
   * coincidencias se podria acabar eligiendo una opcion que no esta en la
   * lista y que el usuario no ha llegado a ver.
   */
  const mostradas = useMemo(() => filtradas.slice(0, MAX_VISIBLES), [filtradas]);

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  // Con el teclado, la opcion resaltada no se puede quedar fuera de la vista.
  useEffect(() => {
    if (!abierto) return;
    lista.current?.querySelector('.is-resaltada')?.scrollIntoView({ block: 'nearest' });
  }, [abierto, resaltado]);

  const elegir = (v: string) => {
    onChange(v);
    setTexto('');
    setAbierto(false);
  };

  return (
    <div className="buscador" ref={caja}>
      <input
        id={id}
        className={`${className ?? 'input'} buscador__campo`}
        type="text"
        autoComplete="off"
        role="combobox"
        aria-expanded={abierto}
        aria-controls={`${id}-lista`}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        disabled={disabled}
        // Cerrado ensenya lo elegido; abierto, lo que se va escribiendo.
        value={abierto ? texto : (elegido?.label ?? '')}
        placeholder={abierto && elegido ? elegido.label : (placeholder ?? 'Escribe para buscar…')}
        onFocus={() => {
          setAbierto(true);
          setTexto('');
          setResaltado(Math.max(0, options.findIndex((o) => o.value === value)));
        }}
        // Con el foco ya puesto (p. ej. tras Escape) un clic no dispara onFocus.
        onClick={() => setAbierto(true)}
        onBlur={(event) => {
          // Tab fuera cierra; un clic en una opcion de la propia lista, no.
          if (!caja.current?.contains(event.relatedTarget as Node)) setAbierto(false);
        }}
        onChange={(event) => {
          setTexto(event.target.value);
          setAbierto(true);
          setResaltado(0);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setAbierto(true);
            setResaltado((i) => {
              const paso = event.key === 'ArrowDown' ? 1 : -1;
              // Sobre las que SE VEN, no sobre todas las que coinciden: si
              // no, bajando lo suficiente se resalta —y con Enter se elige—
              // una receta que no esta en pantalla.
              const total = mostradas.length;
              return total === 0 ? 0 : (i + paso + total) % total;
            });
          } else if (event.key === 'Enter' && abierto) {
            event.preventDefault();
            const opcion = mostradas[Math.min(resaltado, mostradas.length - 1)];
            if (opcion) elegir(opcion.value);
          } else if (event.key === 'Escape' && abierto) {
            event.stopPropagation();
            setAbierto(false);
          }
        }}
      />
      {abierto && (
        <ul className="buscador__lista" id={`${id}-lista`} role="listbox" ref={lista}>
          {filtradas.length === 0 && <li className="buscador__vacio">Nada coincide</li>}
          {mostradas.map((option, i) => (
            <li key={option.value}>
              {option.group && option.group !== mostradas[i - 1]?.group && (
                <span className="buscador__grupo">{option.group}</span>
              )}
              <button
                type="button"
                role="option"
                tabIndex={-1}
                aria-selected={option.value === value}
                className={`buscador__opcion ${i === resaltado ? 'is-resaltada' : ''}`.trim()}
                onMouseEnter={() => setResaltado(i)}
                onClick={() => elegir(option.value)}
              >
                {option.label}
                {option.contexto && texto.trim() !== '' && <span className="buscador__contexto"> — {option.contexto}</span>}
              </button>
            </li>
          ))}
          {filtradas.length > MAX_VISIBLES && (
            <li className="buscador__vacio">
              Y {filtradas.length - MAX_VISIBLES} más: afina la búsqueda.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * Desplegable con busqueda por texto, con su etiqueta.
 *
 * Un `<select>` con doscientas recetas es inservible: hay que recorrerlo
 * entero para encontrar una (Alex, 1 sep 2026). Aqui se escribe "croq" y
 * quedan las croquetas. Sin dependencias: es un input con una lista filtrada.
 *
 * La lista se ordena alfabeticamente por quien la pasa, no aqui: el orden
 * puede tener sentido propio en otros sitios.
 */
export function SearchSelectField({
  label,
  hint,
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: SearchSelectProps): JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <Buscador
        id={id}
        value={value}
        onChange={onChange}
        options={options}
        disabled={disabled}
        placeholder={placeholder}
        describedBy={hint ? hintId : undefined}
      />
      {hint && (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      )}
    </div>
  );
}
